import { Injectable, computed, inject, Injector, signal } from '@angular/core';
import { CompanyReference } from '../feature-services/common-service.models';
import { CommonService } from '../feature-services/common.service';
import { IndexedDbStorageService, DEFAULT_CACHE_STORE } from '../state/storage/indexed-db.service';
import { DataScope, DataScopeLevel, CompanyCatalogItem } from '../models/gars-permissions.model';
import { extractApiErrorMessage } from '../shared/utils/error-handler.util';

/**
 * Maps the raw `entityRegion` codes returned by `/gemini/company-references`
 * to human-friendly display names. Values used consistently across the app
 * (Workspace Adoption dashboard, Gemini reports, authorization scopes...).
 */
export const REGION_NAME_MAP: Record<string, string> = {
  AP: 'Asia Pacific',
  CORP: 'Corporate',
  EU: 'Europe',
  Elevate: 'Elevate',
  IN: 'India',
  LA: 'Latin America',
  MEA: 'Middle East & Africa',
  Trading: 'Trading',
  NA: 'North America',
  AMEA: 'Middle East & Africa',
  Corporate: 'Corporate',
  '-': 'Global',
};

/** Reverse of {@link REGION_NAME_MAP} (display name -> raw region code). */
export const REGION_CODE_MAP: Record<string, string> = {
  ...Object.fromEntries(Object.entries(REGION_NAME_MAP).map(([code, name]) => [name, code])),
  'Asia Pacific': 'AP',
  Asia: 'AP',
  AP: 'AP',
  Corporate: 'CORP',
  'Middle East & Africa': 'MEA',
  AMEA: 'MEA',
  MEA: 'MEA',
  CORP: 'CORP',
  Trading: 'Trading',
  Elevate: 'Elevate',
};

export function regionDisplayName(code: string | undefined | null): string {
  if (!code) return '';
  const trimmed = code.trim();
  return trimmed === '-' ? '' : trimmed;
}

export function resolveRegionCode(nameOrCode: string | undefined | null): string {
  if (!nameOrCode) return '';
  const trimmed = nameOrCode.trim();
  if (REGION_CODE_MAP[trimmed]) return REGION_CODE_MAP[trimmed];
  const lower = trimmed.toLowerCase();
  for (const [name, code] of Object.entries(REGION_CODE_MAP)) {
    if (name.toLowerCase() === lower || code.toLowerCase() === lower) {
      return code;
    }
  }
  return trimmed;
}

export interface CanonicalCompanyMeta {
  name: string;
  code: string;
  country?: string;
  region?: string;
  aliases?: string[];
}

export const CANONICAL_COMPANIES: CanonicalCompanyMeta[] = [];

/**
 * Company code aliases where permission codes differ from catalog codes
 * (e.g. New Zealand permission code is NZL while catalog company code is MNZL).
 */
export const COMPANY_CODE_ALIASES: Record<string, string> = {
  MNZL: 'NZL',
  NZL: 'MNZL',
};

export function isCompanyCodeMatch(a: string | undefined | null, b: string | undefined | null): boolean {
  if (!a || !b) return false;
  const aU = a.trim().toUpperCase();
  const bU = b.trim().toUpperCase();
  if (aU === bU) return true;
  if (COMPANY_CODE_ALIASES[aU] === bU || COMPANY_CODE_ALIASES[bU] === aU) return true;
  return false;
}

export const INVALID_COMPANY_CODES = new Set<string>([
  'GLOBAL', 'N/A', 'NA', 'NONE', 'NULL', 'UNDEFINED', '-'
]);

export function isValidCompanyCode(code: string | undefined | null): boolean {
  if (!code) return false;
  const trimmed = code.trim().toUpperCase();
  if (!trimmed || trimmed === '-' || trimmed.length < 2 || trimmed.length > 8) return false;
  if (INVALID_COMPANY_CODES.has(trimmed)) return false;
  if (!/^[A-Z0-9_-]{2,8}$/.test(trimmed)) return false;
  return true;
}

export function normalizeCompanyName(name: string | undefined | null): string {
  if (!name) return '';
  return name
    .toLowerCase()
    .replace(/\b(ag|sa|ltd|limited|gmbh|llc|bv|b\.v\.|inc|inc\.|corp|s\.a\.|co\.|sl|s\.l\.|srl|s\.r\.l\.|spa|s\.p\.a\.)\b/gi, '')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function lookupCanonicalCompany(query: string | undefined | null): CanonicalCompanyMeta | undefined {
  return undefined;
}

export function deriveCompanyCode(name: string | undefined | null): string {
  if (!name) return '';
  const trimmed = name.trim();
  if (!trimmed) return '';

  const canon = lookupCanonicalCompany(trimmed);
  if (canon?.code && isValidCompanyCode(canon.code)) {
    return canon.code.toUpperCase();
  }

  if (isValidCompanyCode(trimmed)) {
    return trimmed.toUpperCase();
  }

  return '';
}

export function normalizeCompanyReference(raw: any): CompanyReference {
  if (!raw) return raw;
  const rawCode = (raw.companyCode || raw.company_code || raw.code || '').trim();
  const companyName = (raw.companyName || raw.company_name || raw.name || '').trim();
  let entityCountry = (raw.entityCountry || raw.entity_country || raw.country || '').trim();
  let entityRegion = (raw.entityRegion || raw.entity_region || raw.region || '').trim();
  const id = raw.id ?? 0;
  const internalOu = (raw.internalOu || raw.internal_ou || '').trim();
  const entityAsfCode = (raw.entityAsfCode || raw.entity_asf_code || '').trim();

  let code = '';
  if (isValidCompanyCode(rawCode)) {
    code = rawCode.toUpperCase();
  } else if (companyName) {
    const canon = lookupCanonicalCompany(companyName);
    if (canon?.code && isValidCompanyCode(canon.code)) {
      code = canon.code.toUpperCase();
    }
  }

  // Singapore normalization: HTRA, HTSP, LISS or country Singapore belong to Singapore under AP
  const isSingaporeComp = code === 'HTRA' || code === 'HTSP' || code === 'LISS';
  if (isSingaporeComp || entityCountry.toLowerCase() === 'singapore') {
    entityCountry = 'Singapore';
    entityRegion = 'AP';
  }

  const name = companyName || (code ? (lookupCanonicalCompany(code)?.name || code) : '');

  return {
    id,
    companyCode: code,
    companyName: name,
    internalOu,
    entityAsfCode: entityAsfCode || code,
    entityCountry,
    entityRegion,
  };
}

function validReferences(refs: any[]): CompanyReference[] {
  return (refs ?? [])
    .map(normalizeCompanyReference)
    .filter(r => r && (!!r.companyCode || !!r.companyName));
}

const COMPANY_REFS_CACHE_KEY = 'company_references_cache';

/**
 * Shared, cached store for the `GET /gemini/company-references` response.
 * Backed by IndexedDB caching for large-capacity, instant offline/cached boot.
 */
@Injectable({ providedIn: 'root' })
export class CompanyReferencesStore {
  private readonly injector = inject(Injector);
  private _common?: CommonService;
  private get common(): CommonService {
    if (!this._common) {
      this._common = this.injector.get(CommonService);
    }
    return this._common;
  }
  private readonly indexedDb = inject(IndexedDbStorageService);

  /** Raw payload from the endpoint; hydrated immediately from IndexedDB if available. */
  readonly references = signal<CompanyReference[]>([]);

  /** Catalog companies from permissions GET / User Permissions snapshot */
  readonly catalogCompanies = signal<CompanyCatalogItem[]>([]);

  /** Registers or updates companies returned by the permissions GET snapshot */
  registerCatalogCompanies(catalog: CompanyCatalogItem[] | null | undefined): void {
    if (!catalog || !Array.isArray(catalog)) return;
    const normalized = catalog.map(c => {
      const code = (c.companyCode || c.company_code || '').trim().toUpperCase();
      let country = (c.country ?? '').trim();
      let region = (c.region ?? '').trim();
      const isSingaporeComp = code === 'HTRA' || code === 'HTSP' || code === 'LISS';
      if (isSingaporeComp || country.toLowerCase() === 'singapore') {
        country = 'Singapore';
        region = 'AP';
      }
      return {
        ...c,
        companyCode: code || c.companyCode,
        country,
        region,
      };
    });
    this.catalogCompanies.set(normalized);
  }

  /** true once the initial fetch or cache hydration resolved. */
  readonly loaded = signal<boolean>(false);

  /** true while an API refresh is in flight. */
  readonly loading = signal(false);

  readonly error = signal<unknown>(null);

  /** Unique region display names, sorted, directly from response without synthetic overrides. */
  readonly regionNames = computed<string[]>(() => {
    const names = new Set<string>();
    this.references().forEach(r => {
      const name = regionDisplayName(r.entityRegion);
      if (name) names.add(name);
    });
    this.catalogCompanies().forEach(c => {
      const name = regionDisplayName(c.region);
      if (name) names.add(name);
    });
    return [...names].sort();
  });

  /**
   * Region display name -> sorted unique country strings.
   * Country entries of '-' (global/na entities) and blanks are skipped, exactly
   * like the Gemini reports filter does.
   */
  readonly regionsByCountry = computed<Record<string, string[]>>(() => {
    const map: Record<string, Set<string>> = {};
    this.references().forEach(r => {
      const country = (r.entityCountry ?? '').trim();
      if (!country || country === '-' || country.toLowerCase() === 'n/a') return;
      const region = regionDisplayName(r.entityRegion);
      if (!region) return;
      (map[region] ??= new Set()).add(country);
    });
    this.catalogCompanies().forEach(c => {
      const country = (c.country ?? '').trim();
      if (!country || country === '-' || country.toLowerCase() === 'n/a') return;
      const region = regionDisplayName(c.region);
      if (!region) return;
      (map[region] ??= new Set()).add(country);
    });
    // Ensure Singapore is registered under AP
    (map['AP'] ??= new Set()).add('Singapore');
    const out: Record<string, string[]> = {};
    Object.keys(map).forEach(region => {
      out[region] = [...map[region]].sort();
    });
    return out;
  });

  /** Region display name + country -> sorted unique company names. */
  readonly companiesByRegionCountry = computed<Record<string, Record<string, string[]>>>(() => {
    const map: Record<string, Record<string, Set<string>>> = {};
    this.references().forEach(r => {
      const country = (r.entityCountry ?? '').trim();
      if (!country || country === '-' || country.toLowerCase() === 'n/a') return;
      const name = (r.companyName ?? '').trim();
      if (!name) return;
      const region = regionDisplayName(r.entityRegion);
      if (!region) return;
      (map[region] ??= {});
      (map[region][country] ??= new Set()).add(name);
    });
    this.catalogCompanies().forEach(c => {
      const country = (c.country ?? '').trim();
      if (!country || country === '-' || country.toLowerCase() === 'n/a') return;
      const name = (c.companyName || c.company_name || c.companyCode || c.company_code || '').trim();
      if (!name) return;
      const region = regionDisplayName(c.region);
      if (!region) return;
      (map[region] ??= {});
      (map[region][country] ??= new Set()).add(name);
    });
    // Ensure Singapore companies are registered under AP -> Singapore
    (map['AP'] ??= {});
    const sgComps = (map['AP']['Singapore'] ??= new Set());
    ['HTRA', 'HTSP', 'LISS'].forEach(code => {
      const ref = this.references().find(r => r.companyCode === code);
      const cat = this.catalogCompanies().find(c => (c.companyCode || c.company_code) === code);
      const name = ref?.companyName || cat?.companyName || (cat as any)?.company_name;
      if (name) sgComps.add(name);
      else if (ref || cat) sgComps.add(code);
    });
    const out: Record<string, Record<string, string[]>> = {};
    Object.keys(map).forEach(region => {
      out[region] = {};
      Object.keys(map[region]).forEach(country => {
        out[region][country] = [...map[region][country]].sort();
      });
    });
    return out;
  });

  /** Country name (across all regions) -> sorted unique company names. */
  readonly companiesByCountry = computed<Record<string, string[]>>(() => {
    const map: Record<string, Set<string>> = {};
    this.references().forEach(r => {
      const country = (r.entityCountry ?? '').trim();
      if (!country || country === '-' || country.toLowerCase() === 'n/a') return;
      const name = (r.companyName ?? '').trim();
      if (!name) return;
      (map[country] ??= new Set()).add(name);
    });
    this.catalogCompanies().forEach(c => {
      const country = (c.country ?? '').trim();
      if (!country || country === '-' || country.toLowerCase() === 'n/a') return;
      const name = (c.companyName || c.company_name || c.companyCode || c.company_code || '').trim();
      if (!name) return;
      (map[country] ??= new Set()).add(name);
    });
    const out: Record<string, string[]> = {};
    Object.keys(map).forEach(country => {
      out[country] = [...map[country]].sort();
    });
    return out;
  });

  /** Unique country display names, sorted. */
  readonly countryNames = computed<string[]>(() => {
    return Object.keys(this.companiesByCountry()).sort();
  });

  /** Unique company display names across all countries, sorted. */
  readonly allCompanyNames = computed<string[]>(() => {
    const names = new Set<string>();
    this.references().forEach(r => {
      const name = (r.companyName ?? '').trim();
      if (name) names.add(name);
    });
    this.catalogCompanies().forEach(c => {
      const name = (c.companyName || c.company_name || '').trim();
      if (name) names.add(name);
    });
    return [...names].sort();
  });

  /** Unique valid company codes across all countries, sorted. */
  readonly allCompanyCodes = computed<string[]>(() => {
    const codes = new Set<string>();
    this.references().forEach(r => {
      const code = (r.companyCode ?? '').trim().toUpperCase();
      if (code && isValidCompanyCode(code)) codes.add(code);
    });
    this.catalogCompanies().forEach(c => {
      const code = (c.companyCode || c.company_code || '').trim().toUpperCase();
      if (code && isValidCompanyCode(code)) codes.add(code);
    });
    return [...codes].sort();
  });

  /** Find region display name for a given country. */
  getRegionForCountry(countryName: string): string | undefined {
    if (!countryName) return undefined;
    const regions = this.getRegionsForCountry(countryName);
    return regions.length > 0 ? regions[0] : undefined;
  }

  /** Find all region display names for a given country. */
  getRegionsForCountry(countryName: string): string[] {
    if (!countryName) return [];
    const q = countryName.trim().toLowerCase();
    const regions = new Set<string>();

    const byCountry = this.regionsByCountry();
    for (const [reg, countryList] of Object.entries(byCountry)) {
      if (countryList.some(c => c.trim().toLowerCase() === q)) {
        regions.add(reg);
      }
    }

    this.references().forEach(r => {
      if ((r.entityCountry ?? '').trim().toLowerCase() === q) {
        const reg = regionDisplayName(r.entityRegion);
        if (reg) regions.add(reg);
      }
    });

    this.catalogCompanies().forEach(c => {
      if ((c.country ?? '').trim().toLowerCase() === q) {
        const reg = regionDisplayName(c.region);
        if (reg) regions.add(reg);
      }
    });

    if (regions.size === 0 && q === 'singapore') {
      regions.add('AP');
    }
    if (q === 'singapore') {
      regions.add('AP');
    }

    return Array.from(regions);
  }

  /**
   * Returns all known company codes for a given country.
   */
  getCompanyCodesForCountry(countryName: string): string[] {
    if (!countryName) return [];
    const target = countryName.trim().toLowerCase();
    const codes = new Set<string>();

    if (target === 'singapore') {
      ['HTRA', 'HTSP', 'LISS'].forEach(c => codes.add(c));
    }

    for (const r of this.references()) {
      if ((r.entityCountry ?? '').trim().toLowerCase() === target) {
        const code = (r.companyCode ?? '').trim().toUpperCase();
        if (code && isValidCompanyCode(code)) codes.add(code);
      }
    }

    for (const cat of this.catalogCompanies()) {
      if ((cat.country ?? '').trim().toLowerCase() === target) {
        const code = (cat.companyCode || cat.company_code || '').trim().toUpperCase();
        if (code && isValidCompanyCode(code)) codes.add(code);
      }
    }

    return Array.from(codes);
  }

  /**
   * Returns all known country names for a given region (display name or raw code).
   */
  getCountriesForRegion(regionNameOrCode: string): string[] {
    if (!regionNameOrCode) return [];
    const targetCode = resolveRegionCode(regionNameOrCode);
    const targetName = regionDisplayName(regionNameOrCode);
    const countries = new Set<string>();

    if (targetCode === 'AP' || (targetName && targetName.toUpperCase() === 'AP')) {
      countries.add('Singapore');
    }

    const byCountry = this.regionsByCountry();
    for (const [reg, countryList] of Object.entries(byCountry)) {
      if (
        (targetCode && resolveRegionCode(reg) === targetCode) ||
        (targetName && reg.toLowerCase() === targetName.toLowerCase())
      ) {
        countryList.forEach(c => countries.add(c.trim()));
      }
    }

    for (const r of this.references()) {
      const reg = r.entityRegion;
      if (
        (targetCode && resolveRegionCode(reg) === targetCode) ||
        (targetName && regionDisplayName(reg).toLowerCase() === targetName.toLowerCase())
      ) {
        const c = (r.entityCountry ?? '').trim();
        if (c && c !== '-' && c.toLowerCase() !== 'n/a') countries.add(c);
      }
    }

    for (const cat of this.catalogCompanies()) {
      const reg = cat.region;
      if (
        (targetCode && resolveRegionCode(reg) === targetCode) ||
        (targetName && regionDisplayName(reg).toLowerCase() === targetName.toLowerCase())
      ) {
        const c = (cat.country ?? '').trim();
        if (c && c !== '-' && c.toLowerCase() !== 'n/a') countries.add(c);
      }
    }

    return Array.from(countries);
  }

  /**
   * Resolves a company code or name to its canonical uppercase company code.
   * Returns an empty string if unresolvable or invalid.
   */
  resolveCanonicalCompanyCode(nameOrCode: string | undefined | null): string {
    if (!nameOrCode) return '';
    const trimmed = nameOrCode.trim();
    if (!trimmed) return '';

    // If formatted like "CompanyName (CODE)"
    const parenMatch = trimmed.match(/^.+?\s*\(([A-Za-z0-9_-]+)\)$/);
    if (parenMatch && isValidCompanyCode(parenMatch[1])) {
      return parenMatch[1].trim().toUpperCase();
    }

    const canon = lookupCanonicalCompany(trimmed);
    if (canon?.code && isValidCompanyCode(canon.code)) {
      return canon.code.trim().toUpperCase();
    }

    const upper = trimmed.toUpperCase();
    if (COMPANY_CODE_ALIASES[upper]) {
      const alias = COMPANY_CODE_ALIASES[upper];
      for (const cat of this.catalogCompanies()) {
        const code = (cat.companyCode || cat.company_code || '').trim().toUpperCase();
        if (code === upper || code === alias) {
          return code;
        }
      }
    }

    for (const cat of this.catalogCompanies()) {
      const code = (cat.companyCode || cat.company_code || '').trim().toUpperCase();
      const name = (cat.companyName || cat.company_name || '').trim();
      if (code && isValidCompanyCode(code)) {
        if (code.toLowerCase() === trimmed.toLowerCase() || (name && name.toLowerCase() === trimmed.toLowerCase())) {
          return code;
        }
      }
    }

    const ref = this.findReference(trimmed);
    if (ref?.companyCode && isValidCompanyCode(ref.companyCode)) {
      return ref.companyCode.trim().toUpperCase();
    }

    if (isValidCompanyCode(trimmed)) {
      return trimmed.toUpperCase();
    }

    return '';
  }

  /**
   * Returns all known company codes for a given region (display name or raw code).
   * Only includes companies that directly belong to this region.
   */
  getCompanyCodesForRegion(regionNameOrCode: string): string[] {
    if (!regionNameOrCode) return [];
    const codes = new Set<string>();

    const targetCode = resolveRegionCode(regionNameOrCode);
    const targetName = regionDisplayName(regionNameOrCode);
    const isGlobal =
      regionNameOrCode === '-' ||
      regionNameOrCode.toLowerCase() === 'global' ||
      targetCode === '-';

    if (targetCode === 'AP' || (targetName && targetName.toUpperCase() === 'AP')) {
      ['HTRA', 'HTSP', 'LISS'].forEach(c => codes.add(c));
    }

    for (const ref of this.references()) {
      const reg = ref.entityRegion;
      const match =
        isGlobal ||
        (targetCode && resolveRegionCode(reg) === targetCode) ||
        (targetName && regionDisplayName(reg).toLowerCase() === targetName.toLowerCase());
      if (match && ref.companyCode && isValidCompanyCode(ref.companyCode)) {
        codes.add(ref.companyCode.trim().toUpperCase());
      }
    }

    for (const cat of this.catalogCompanies()) {
      const reg = cat.region;
      const match =
        isGlobal ||
        (targetCode && resolveRegionCode(reg) === targetCode) ||
        (targetName && regionDisplayName(reg).toLowerCase() === targetName.toLowerCase());
      const catCode = (cat.companyCode || cat.company_code || '').trim().toUpperCase();
      if (match && catCode && isValidCompanyCode(catCode)) {
        codes.add(catCode);
      }
    }

    return Array.from(codes);
  }

  /**
   * Returns all known company codes that belong to BOTH a given region and country.
   */
  getCompanyCodesForRegionCountry(regionNameOrCode: string, countryName: string): string[] {
    if (!regionNameOrCode || !countryName) return [];
    const targetCode = resolveRegionCode(regionNameOrCode);
    const targetName = regionDisplayName(regionNameOrCode);
    const targetCountry = countryName.trim().toLowerCase();
    const isGlobal =
      regionNameOrCode === '-' ||
      regionNameOrCode.toLowerCase() === 'global' ||
      targetCode === '-';

    const codes = new Set<string>();

    if ((targetCode === 'AP' || (targetName && targetName.toUpperCase() === 'AP')) && targetCountry === 'singapore') {
      ['HTRA', 'HTSP', 'LISS'].forEach(c => codes.add(c));
    }

    for (const ref of this.references()) {
      const reg = ref.entityRegion;
      const matchRegion =
        isGlobal ||
        (targetCode && resolveRegionCode(reg) === targetCode) ||
        (targetName && regionDisplayName(reg).toLowerCase() === targetName.toLowerCase());
      const matchCountry = (ref.entityCountry ?? '').trim().toLowerCase() === targetCountry;
      if (matchRegion && matchCountry && ref.companyCode && isValidCompanyCode(ref.companyCode)) {
        codes.add(ref.companyCode.trim().toUpperCase());
      }
    }

    for (const cat of this.catalogCompanies()) {
      const reg = cat.region;
      const matchRegion =
        isGlobal ||
        (targetCode && resolveRegionCode(reg) === targetCode) ||
        (targetName && regionDisplayName(reg).toLowerCase() === targetName.toLowerCase());
      const matchCountry = (cat.country ?? '').trim().toLowerCase() === targetCountry;
      const catCode = (cat.companyCode || cat.company_code || '').trim().toUpperCase();
      if (matchRegion && matchCountry && catCode && isValidCompanyCode(catCode)) {
        codes.add(catCode);
      }
    }

    return Array.from(codes);
  }

  /**
   * Resolves country display name for a given company code or name.
   */
  getCountryForCompany(nameOrCode: string): string | undefined {
    if (!nameOrCode) return undefined;
    const isSg = ['HTRA', 'HTSP', 'LISS'].includes(nameOrCode.trim().toUpperCase());
    if (isSg) return 'Singapore';
    const ref = this.findReference(nameOrCode);
    if (ref?.entityCountry && ref.entityCountry !== '-' && ref.entityCountry.toLowerCase() !== 'n/a') {
      return ref.entityCountry.trim();
    }
    const canon = lookupCanonicalCompany(nameOrCode);
    if (canon?.country && canon.country !== '-' && canon.country.toLowerCase() !== 'n/a') {
      return canon.country.trim();
    }
    return undefined;
  }

  /**
   * Resolves region display name for a given company code or name.
   */
  getRegionForCompany(nameOrCode: string): string | undefined {
    if (!nameOrCode) return undefined;
    const isSg = ['HTRA', 'HTSP', 'LISS'].includes(nameOrCode.trim().toUpperCase());
    if (isSg) return 'AP';
    const ref = this.findReference(nameOrCode);
    if (ref?.entityRegion) {
      const regName = regionDisplayName(ref.entityRegion);
      if (regName) return regName;
    }
    const canon = lookupCanonicalCompany(nameOrCode);
    if (canon?.region) {
      const regName = regionDisplayName(canon.region);
      if (regName) return regName;
    }
    return undefined;
  }

  /**
   * Outbound Scope Resolution:
   * Translates a DataScope object into the complete, canonical list of authorized company codes
   * to send to the authorization backend.
   */
  resolveCompanyCodesForDataScope(scope: DataScope | null | undefined): string[] {
    if (!scope || scope.level === 'global') return [];
    if (scope.level === 'company') {
      const codes = new Set<string>();
      for (const comp of scope.allowedCompanies || []) {
        const canon = this.resolveCanonicalCompanyCode(comp) || (isValidCompanyCode(comp) ? comp.trim().toUpperCase() : '');
        if (canon && isValidCompanyCode(canon)) codes.add(canon);
      }
      return Array.from(codes);
    }
    if (scope.level === 'country') {
      const codes = new Set<string>();
      for (const country of scope.allowedCountries || []) {
        for (const c of this.getCompanyCodesForCountry(country)) {
          const canon = this.resolveCanonicalCompanyCode(c) || (isValidCompanyCode(c) ? c.trim().toUpperCase() : '');
          if (canon && isValidCompanyCode(canon)) codes.add(canon);
        }
      }
      for (const comp of scope.allowedCompanies || []) {
        const canon = this.resolveCanonicalCompanyCode(comp) || (isValidCompanyCode(comp) ? comp.trim().toUpperCase() : '');
        if (canon && isValidCompanyCode(canon)) codes.add(canon);
      }
      return Array.from(codes);
    }
    if (scope.level === 'region') {
      const codes = new Set<string>();
      for (const reg of scope.allowedRegions || []) {
        for (const c of this.getCompanyCodesForRegion(reg)) {
          const canon = this.resolveCanonicalCompanyCode(c) || (isValidCompanyCode(c) ? c.trim().toUpperCase() : '');
          if (canon && isValidCompanyCode(canon)) codes.add(canon);
        }
      }
      for (const comp of scope.allowedCompanies || []) {
        const canon = this.resolveCanonicalCompanyCode(comp) || (isValidCompanyCode(comp) ? comp.trim().toUpperCase() : '');
        if (canon && isValidCompanyCode(canon)) codes.add(canon);
      }
      return Array.from(codes);
    }
    return [];
  }

  /**
   * Inbound Reverse Classification & Grouping:
   * Evaluates the COMPLETE set of authorized company codes returned by authorization service
   * and dynamically reconstructs whether the resulting scope is Region-Based, Country-Based,
   * Company-Based, or Global.
   *
   * Business Rules:
   * 1. Evaluates the complete set of authorized companies holistically.
   * 2. Region-Based Grouping: If all authorized companies fall under region-level consideration
   *    (i.e. every authorized company belongs to fully covered regions, with 100% region coverage
   *    and no partial leftovers).
   * 3. Country-Based Grouping: If all authorized companies fall under country-level consideration
   *    (i.e. every authorized company belongs to fully covered countries, with 100% country coverage
   *    and no partial leftovers).
   * 4. Mixed / Partial Scenarios: If authorized companies span multiple countries or regions with partial
   *    coverage, it remains company-level scoped to preserve exact security boundaries.
   * 5. Global: Unscoped access or when all regions/companies across the enterprise are covered.
   */
  inferDataScopeFromCompanyCodes(
    companyCodes: string[] | null | undefined,
    role?: string | null,
    hints?: { region?: string; country?: string; dataScopeLevel?: DataScopeLevel }
  ): DataScope {
    const rawList = companyCodes || [];
    const canonSet = new Set<string>();
    for (const raw of rawList) {
      const canon = this.resolveCanonicalCompanyCode(raw);
      if (canon) {
        canonSet.add(canon);
        if (COMPANY_CODE_ALIASES[canon]) canonSet.add(COMPANY_CODE_ALIASES[canon]);
      } else if (isValidCompanyCode(raw)) {
        const u = raw.trim().toUpperCase();
        canonSet.add(u);
        if (COMPANY_CODE_ALIASES[u]) canonSet.add(COMPANY_CODE_ALIASES[u]);
      }
    }
    const authorizedCodes = Array.from(canonSet);
    const isSuper = role === 'Super Admin';
    const desiredLevel = hints?.dataScopeLevel;

    const isCodeCovered = (code: string): boolean => {
      const u = (code || '').trim().toUpperCase();
      return canonSet.has(u) || (!!COMPANY_CODE_ALIASES[u] && canonSet.has(COMPANY_CODE_ALIASES[u]));
    };

    // Handle empty company codes list
    if (authorizedCodes.length === 0) {
      if (isSuper || desiredLevel === 'global') {
        return { level: 'global', allowedRegions: [], allowedCountries: [], allowedCompanies: [] };
      }
      if (desiredLevel === 'country' || (!desiredLevel && hints?.country)) {
        return {
          level: 'country',
          allowedCountries: hints?.country ? [hints.country] : [],
          allowedRegions: hints?.region ? [hints.region] : [],
          allowedCompanies: [],
        };
      }
      if (desiredLevel === 'region' || (!desiredLevel && hints?.region)) {
        return {
          level: 'region',
          allowedRegions: hints?.region ? [hints.region] : [],
          allowedCountries: [],
          allowedCompanies: [],
        };
      }
      if (desiredLevel === 'company') {
        return {
          level: 'company',
          allowedCompanies: [],
          allowedCountries: hints?.country ? [hints.country] : [],
          allowedRegions: hints?.region ? [hints.region] : [],
        };
      }
      return { level: 'global', allowedRegions: [], allowedCountries: [], allowedCompanies: [] };
    }

    const fullyCoveredCountries = new Set<string>();
    const fullyCoveredRegions = new Set<string>();

    for (const code of authorizedCodes) {
      const ctry = this.getCountryForCompany(code);
      if (ctry && !fullyCoveredCountries.has(ctry)) {
        const ctryCodes = this.getCompanyCodesForCountry(ctry);
        if (ctryCodes.length > 0 && ctryCodes.every(c => isCodeCovered(c))) {
          fullyCoveredCountries.add(ctry);
        }
      }
      const reg = this.getRegionForCompany(code);
      if (reg && !fullyCoveredRegions.has(reg)) {
        const regCodes = this.getCompanyCodesForRegion(reg);
        const regCountries = this.getCountriesForRegion(reg);
        const allCompsCovered = regCodes.length > 0 && regCodes.every(c => isCodeCovered(c));
        const allCtrysCovered = regCountries.length === 0 || regCountries.every(c => {
          const cCodes = this.getCompanyCodesForRegionCountry(reg, c);
          return cCodes.length === 0 || cCodes.every(cc => isCodeCovered(cc));
        });
        if (allCompsCovered && allCtrysCovered) {
          fullyCoveredRegions.add(reg);
        }
      }
    }

    // ── If desiredLevel is explicitly specified, honor it strictly ──
    if (desiredLevel === 'company') {
      return {
        level: 'company',
        allowedCompanies: authorizedCodes,
        allowedCountries: Array.from(fullyCoveredCountries),
        allowedRegions: Array.from(fullyCoveredRegions),
      };
    }
    if (desiredLevel === 'country') {
      return {
        level: 'country',
        allowedCountries: fullyCoveredCountries.size > 0 ? Array.from(fullyCoveredCountries) : (hints?.country ? [hints.country] : []),
        allowedRegions: Array.from(fullyCoveredRegions),
        allowedCompanies: authorizedCodes,
      };
    }
    if (desiredLevel === 'region') {
      return {
        level: 'region',
        allowedRegions: fullyCoveredRegions.size > 0 ? Array.from(fullyCoveredRegions) : (hints?.region ? [hints.region] : []),
        allowedCountries: Array.from(fullyCoveredCountries).filter(ctry => {
          const regs = this.getRegionsForCountry(ctry);
          return !regs.some(r => {
            const rCode = resolveRegionCode(r).toLowerCase();
            return Array.from(fullyCoveredRegions).some(f => resolveRegionCode(f).toLowerCase() === rCode || f.toLowerCase() === r.toLowerCase());
          });
        }),
        allowedCompanies: authorizedCodes,
      };
    }
    if (desiredLevel === 'global') {
      return {
        level: 'global',
        allowedRegions: [],
        allowedCountries: [],
        allowedCompanies: authorizedCodes,
      };
    }

    // ── Global Scope Check: If all enterprise regions are covered, classify as Global ──
    const allRegions = this.regionNames().filter(r => r !== '-' && r.toLowerCase() !== 'global');
    if (allRegions.length > 0) {
      let allCovered = true;
      for (const reg of allRegions) {
        const regCodes = this.getCompanyCodesForRegion(reg);
        if (regCodes.length > 0 && !regCodes.every(c => isCodeCovered(c))) {
          allCovered = false;
          break;
        }
      }
      if (allCovered) {
        return {
          level: 'global',
          allowedRegions: [],
          allowedCountries: [],
          allowedCompanies: authorizedCodes,
        };
      }
    }

    // ── Check if all authorized codes are completely covered by fullyCoveredRegions ──
    if (fullyCoveredRegions.size > 0) {
      const allRegCodes = new Set<string>();
      for (const reg of fullyCoveredRegions) {
        this.getCompanyCodesForRegion(reg).forEach(c => allRegCodes.add(c));
      }
      if (authorizedCodes.every(c => allRegCodes.has(c) || (!!COMPANY_CODE_ALIASES[c] && allRegCodes.has(COMPANY_CODE_ALIASES[c])))) {
        return {
          level: 'region',
          allowedRegions: Array.from(fullyCoveredRegions),
          allowedCountries: Array.from(fullyCoveredCountries).filter(ctry => {
            const regs = this.getRegionsForCountry(ctry);
            return !regs.some(r => {
              const rCode = resolveRegionCode(r).toLowerCase();
              return Array.from(fullyCoveredRegions).some(f => resolveRegionCode(f).toLowerCase() === rCode || f.toLowerCase() === r.toLowerCase());
            });
          }),
          allowedCompanies: authorizedCodes,
        };
      }
    }

    // ── Check if all authorized codes are completely covered by fullyCoveredCountries ──
    if (fullyCoveredCountries.size > 0) {
      const allCtryCodes = new Set<string>();
      for (const ctry of fullyCoveredCountries) {
        this.getCompanyCodesForCountry(ctry).forEach(c => allCtryCodes.add(c));
      }
      if (authorizedCodes.every(c => allCtryCodes.has(c) || (!!COMPANY_CODE_ALIASES[c] && allCtryCodes.has(COMPANY_CODE_ALIASES[c])))) {
        return {
          level: 'country',
          allowedCountries: Array.from(fullyCoveredCountries),
          allowedRegions: Array.from(fullyCoveredRegions),
          allowedCompanies: authorizedCodes,
        };
      }
    }

    // ── Default for specific company assignments: Company Based ──
    // Retains granular company-level scope so individual company assignments (like HTRA, HTSP)
    // are never falsely collapsed into country or region scope.
    return {
      level: 'company',
      allowedCompanies: authorizedCodes,
      allowedCountries: Array.from(fullyCoveredCountries),
      allowedRegions: Array.from(fullyCoveredRegions),
    };
  }

  /** Find reference item by company name or code. */
  findReference(companyNameOrCode: string): CompanyReference | undefined {
    if (!companyNameOrCode) return undefined;
    const trimmed = companyNameOrCode.trim();
    if (!trimmed) return undefined;

    // Handle strings that already have "Name (CODE)"
    const parenMatch = trimmed.match(/^(.+?)\s*\(([A-Za-z0-9_-]+)\)$/);
    if (parenMatch) {
      const namePart = parenMatch[1].trim();
      const codePart = parenMatch[2].trim();
      const found = this.findReference(namePart) || this.findReference(codePart);
      if (found) return found;
      return {
        id: 0,
        companyName: namePart,
        companyCode: codePart,
        internalOu: '',
        entityAsfCode: codePart,
        entityCountry: '',
        entityRegion: '',
      };
    }

    const q = trimmed.toLowerCase();

    // 1. Direct match on loaded references
    const exact = this.references().find(
      r => (r.companyName ?? '').trim().toLowerCase() === q ||
           (r.companyCode ?? '').trim().toLowerCase() === q
    );
    if (exact) return exact;

    // 2. Direct match on catalog companies (from permissions snapshot)
    const catMatch = this.catalogCompanies().find(
      c => ((c.companyCode || c.company_code) && (c.companyCode || c.company_code)!.trim().toLowerCase() === q) ||
           ((c.companyName || c.company_name) && (c.companyName || c.company_name)!.trim().toLowerCase() === q)
    );
    if (catMatch) {
      const code = (catMatch.companyCode || catMatch.company_code || '').trim().toUpperCase();
      const name = (catMatch.companyName || catMatch.company_name || '').trim();
      return {
        id: 0,
        companyName: name || code,
        companyCode: code,
        internalOu: '',
        entityAsfCode: code,
        entityCountry: catMatch.country || '',
        entityRegion: catMatch.region || '',
      };
    }

    // 2b. Check code aliases
    const alias = COMPANY_CODE_ALIASES[trimmed.toUpperCase()];
    if (alias) {
      const aliasExact = this.references().find(
        r => (r.companyCode ?? '').trim().toUpperCase() === alias
      );
      if (aliasExact) return aliasExact;

      const aliasCat = this.catalogCompanies().find(
        c => (c.companyCode || c.company_code || '').trim().toUpperCase() === alias
      );
      if (aliasCat) {
        const code = (aliasCat.companyCode || aliasCat.company_code || '').trim().toUpperCase();
        const name = (aliasCat.companyName || aliasCat.company_name || '').trim();
        return {
          id: 0,
          companyName: name || code,
          companyCode: code,
          internalOu: '',
          entityAsfCode: code,
          entityCountry: aliasCat.country || '',
          entityRegion: aliasCat.region || '',
        };
      }
    }

    // 3. Normalized name match on loaded references
    const normQ = normalizeCompanyName(trimmed);
    if (normQ) {
      const normMatch = this.references().find(
        r => normalizeCompanyName(r.companyName ?? '') === normQ
      );
      if (normMatch) return normMatch;
    }

    return undefined;
  }

  /**
   * Formats a company name and code into display string: "CompanyName (CompanyCode)".
   * Guarantees that every company displays with its valid code when available.
   */
  formatCompanyDisplay(nameOrCode: string | undefined | null): string {
    if (!nameOrCode) return '';
    const trimmed = nameOrCode.trim();
    if (!trimmed) return '';

    // If already in "Name (CODE)" format:
    const parenMatch = trimmed.match(/^(.+?)\s*\(([A-Za-z0-9_-]+)\)$/);
    if (parenMatch) {
      const namePart = parenMatch[1].trim();
      const codePart = parenMatch[2].trim().toUpperCase();
      if (isValidCompanyCode(codePart)) {
        return `${namePart} (${codePart})`;
      }
      return namePart;
    }

    // 1. Lookup in live references & canonical catalog
    const ref = this.findReference(trimmed);
    if (ref) {
      const name = (ref.companyName ?? '').trim();
      const code = (ref.companyCode ?? '').trim().toUpperCase();
      if (name && code && isValidCompanyCode(code) && name.toLowerCase() !== code.toLowerCase()) {
        return `${name} (${code})`;
      }
      if (name && !code) {
        const canon = lookupCanonicalCompany(name);
        if (canon?.code && isValidCompanyCode(canon.code)) {
          return `${name} (${canon.code.toUpperCase()})`;
        }
        return name;
      }
      if (code && isValidCompanyCode(code)) {
        return code;
      }
    }

    // 2. If it's a short alphanumeric code (e.g. HISA, CH01) without spaces
    if (isValidCompanyCode(trimmed)) {
      const canon = lookupCanonicalCompany(trimmed);
      if (canon && canon.name && canon.code.toLowerCase() === trimmed.toLowerCase()) {
        return `${canon.name} (${canon.code.toUpperCase()})`;
      }
      return trimmed.toUpperCase();
    }

    const canon = lookupCanonicalCompany(trimmed);
    if (canon?.code && isValidCompanyCode(canon.code)) {
      return `${canon.name} (${canon.code.toUpperCase()})`;
    }

    return trimmed;
  }

  private fetched = false;

  constructor() {
    this.hydrateFromIndexedDb().then(() => {
      this.load();
    });
  }

  /**
   * Pre-load cached references from IndexedDB for zero-latency initial rendering.
   */
  private async hydrateFromIndexedDb(): Promise<void> {
    try {
      const cached = await this.indexedDb.get<CompanyReference[]>(COMPANY_REFS_CACHE_KEY, DEFAULT_CACHE_STORE);
      if (cached && Array.isArray(cached) && cached.length > 0 && this.references().length === 0) {
        this.references.set(cached);
        this.loaded.set(true);
      }
    } catch {
      // Ignore cache read failures; network fetch proceeds normally
    }
  }

  /**
   * Refresh from the live `/gemini/company-references` endpoint. A non-empty
   * response replaces the current payload and persists into IndexedDB.
   */
  load(): void {
    if (this.fetched) return;
    this.fetched = true;
    this.loading.set(true);
    this.common.getCompanyHierachy().subscribe({
      next: res => {
        const data = validReferences(res.data ?? ([] as CompanyReference[]));
        if (data.length) {
          this.references.set(data);
          this.error.set(null);
          // Persist large reference hierarchy to IndexedDB
          this.indexedDb.set(COMPANY_REFS_CACHE_KEY, data, DEFAULT_CACHE_STORE).catch(err => {
            console.warn('[CompanyReferencesStore] Error caching in IndexedDB:', err);
          });
        }
      },
      error: err => {
        this.error.set(extractApiErrorMessage(err, 'Failed to fetch company references.'));
      },
      complete: () => {
        this.loading.set(false);
        this.loaded.set(true);
      },
    });
  }

  /** Clear cached server data so a subsequent {@link load} refetches. */
  reload(): void {
    this.fetched = false;
    this.load();
  }
}
